import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  Plus,
  Loader2,
  MoreVertical,
  Send,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Mail,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  FileEdit,
  Ban,
  CircleDashed,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { onboardingAPI, SupplierInvitation } from '../../lib/api';
import { usePagination } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useFormatters } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';

const STATUS_CONFIG: Record<SupplierInvitation['status'], { label: string; className: string; icon: typeof Clock }> = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-800', icon: FileEdit },
  pending_approval: { label: 'Pending Approval', className: 'bg-amber-100 text-amber-800', icon: CircleDashed },
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-800', icon: Clock },
  sent: { label: 'Sent', className: 'bg-blue-100 text-blue-800', icon: Mail },
  opened: { label: 'Opened', className: 'bg-purple-100 text-purple-800', icon: Eye },
  accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800', icon: CheckCircle },
  declined: { label: 'Declined', className: 'bg-red-100 text-red-800', icon: XCircle },
  expired: { label: 'Expired', className: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-600', icon: Ban },
};

const RISK_COLORS: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

export default function InvitationsList() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [invitations, setInvitations] = useState<SupplierInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const pagination = usePagination({ pageSize: 20 });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const searchRef = useRef<HTMLInputElement>(null);
  const currentSearchRef = useRef('');

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, unknown> = {
        page,
        page_size: pagination.pageSize,
        ordering: '-invited_at',
      };

      if (currentSearchRef.current) {
        params['search'] = currentSearchRef.current;
      }
      if (statusFilter !== 'all') {
        params['status'] = statusFilter;
      }

      const response = await onboardingAPI.getInvitations(params);
      setInvitations(response.data.results);
      pagination.setFromResponse(response.data.count, (response.data.results || []).length);
      pagination.setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load invitations'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const value = searchRef.current?.value || '';
    currentSearchRef.current = value;
    pagination.reset();
    fetchInvitations(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    pagination.reset();
    setTimeout(() => fetchInvitations(1), 0);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchInvitations(newPage);
    }
  };

  const handleResend = async (invitation: SupplierInvitation) => {
    const ok = await confirm(`Resend invitation to ${invitation.invitee_email}?`);
    if (!ok) {
      return;
    }

    try {
      setActionLoading(invitation.id);
      await onboardingAPI.resendInvitation(invitation.id);
      fetchInvitations(pagination.currentPage);
    } catch (err) {
      alert(getErrorMessage(err, 'Failed to resend invitation'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (invitation: SupplierInvitation) => {
    const ok = await confirm({ title: 'Cancel Invitation', description: `Cancel invitation for ${invitation.invitee_company_name}? This cannot be undone.`, confirmLabel: 'Cancel Invitation', variant: 'destructive' });
    if (!ok) {
      return;
    }

    try {
      setActionLoading(invitation.id);
      await onboardingAPI.cancelInvitation(invitation.id);
      fetchInvitations(pagination.currentPage);
    } catch (err) {
      alert(getErrorMessage(err, 'Failed to cancel invitation'));
    } finally {
      setActionLoading(null);
    }
  };

  const { formatDateTime: contextFormatDateTime } = useFormatters();

  const handleSubmitForApproval = async (invitation: SupplierInvitation) => {
    try {
      setActionLoading(invitation.id);
      await onboardingAPI.submitForApproval(invitation.id);
      fetchInvitations(pagination.currentPage);
    } catch (err) {
      alert(getErrorMessage(err, 'Failed to submit for approval'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveAndSend = async (invitation: SupplierInvitation) => {
    const ok = await confirm(`Approve and send invitation to ${invitation.invitee_email}?`);
    if (!ok) {
      return;
    }

    try {
      setActionLoading(invitation.id);
      await onboardingAPI.approveToSend(invitation.id);
      fetchInvitations(pagination.currentPage);
    } catch (err) {
      alert(getErrorMessage(err, 'Failed to approve invitation'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectApproval = async (invitation: SupplierInvitation) => {
    const reason = window.prompt('Enter reason for rejection:');
    if (reason === null) return;

    try {
      setActionLoading(invitation.id);
      await onboardingAPI.rejectApproval(invitation.id, reason);
      fetchInvitations(pagination.currentPage);
    } catch (err) {
      alert(getErrorMessage(err, 'Failed to reject invitation'));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return contextFormatDateTime(dateStr);
  };

  const getStatusBadge = (status: SupplierInvitation['status']) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading && invitations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Supplier Invitations</h1>
          <p className="text-muted-foreground mt-2">
            Manage supplier onboarding invitations
          </p>
        </div>
        <Button onClick={() => navigate('/onboarding/invitations/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Invitation
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Invitations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.totalCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">
              {invitations.filter(i => i.status === 'pending' || i.status === 'sent').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {invitations.filter(i => i.status === 'accepted').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {invitations.filter(i => i.status === 'expired').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchRef}
            placeholder="Search by company name or email..."
            onKeyDown={handleSearchKeyDown}
            className="pl-10"
          />
        </div>
        <Button onClick={handleSearch}>
          <Search className="h-4 w-4 mr-2" />
          Search
        </Button>
        <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="opened">Opened</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => fetchInvitations(pagination.currentPage)}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Invitations Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Risk Level</TableHead>
                <TableHead>Invited</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    {currentSearchRef.current || statusFilter !== 'all'
                      ? 'No invitations found matching your filters'
                      : 'No invitations yet. Create your first invitation!'}
                  </TableCell>
                </TableRow>
              ) : (
                invitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">
                      {invitation.invitee_company_name}
                    </TableCell>
                    <TableCell>{invitation.invitee_contact_name}</TableCell>
                    <TableCell>{invitation.invitee_email}</TableCell>
                    <TableCell>{getStatusBadge(invitation.status)}</TableCell>
                    <TableCell>
                      <Badge className={RISK_COLORS[invitation.initial_risk_level]}>
                        {invitation.initial_risk_level.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(invitation.invited_at || invitation.sent_at)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(invitation.expires_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" disabled={actionLoading === invitation.id}>
                            {actionLoading === invitation.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreVertical className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {/* Draft actions */}
                          {invitation.status === 'draft' && (
                            <>
                              <DropdownMenuItem onClick={() => navigate(`/onboarding/invitations/${invitation.id}/edit`)}>
                                <FileEdit className="h-4 w-4 mr-2" />
                                Edit Draft
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleSubmitForApproval(invitation)}>
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Submit for Approval
                              </DropdownMenuItem>
                            </>
                          )}
                          {/* Pending approval actions */}
                          {invitation.status === 'pending_approval' && (
                            <>
                              <DropdownMenuItem onClick={() => handleApproveAndSend(invitation)}>
                                <ThumbsUp className="h-4 w-4 mr-2" />
                                Approve & Send
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleRejectApproval(invitation)} className="text-red-600">
                                <ThumbsDown className="h-4 w-4 mr-2" />
                                Reject
                              </DropdownMenuItem>
                            </>
                          )}
                          {/* Pending (approved but not sent) actions */}
                          {invitation.status === 'pending' && (
                            <DropdownMenuItem onClick={() => onboardingAPI.sendInvitation(invitation.id).then(() => fetchInvitations(pagination.currentPage)).catch((err: unknown) => { console.error('Failed to resend invitation:', err); toast.error('Failed to resend invitation'); })}>
                              <Send className="h-4 w-4 mr-2" />
                              Send Invitation
                            </DropdownMenuItem>
                          )}
                          {/* Resend for sent/opened/expired */}
                          {['sent', 'opened', 'expired'].includes(invitation.status) && (
                            <DropdownMenuItem onClick={() => handleResend(invitation)}>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Resend Invitation
                            </DropdownMenuItem>
                          )}
                          {/* Cancel for non-terminal statuses */}
                          {!['accepted', 'declined', 'expired', 'cancelled'].includes(invitation.status) && (
                            <DropdownMenuItem
                              onClick={() => handleCancel(invitation)}
                              className="text-red-600"
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Cancel Invitation
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.hasMultiplePages && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalCount} total)
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.isFirstPage || loading}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.isLastPage || loading}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
